import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { IconInfoCircle } from '@tabler/icons-react';
import { useMingo } from '../context/MingoContext';
import { recordAdClick, recordAdImpression } from '../utils/adMatcher';
import GoogleAdSense from './GoogleAdSense';

const ADSENSE_VARIANT = { feed: 'card', mini: 'mini', banner: 'banner' };

/**
 * 화면 노출 CPM 광고 카드 — 채팅/커뮤니티/모임/프로필/업체대시보드/지역리스트 등 공용.
 * 노출 시 1회 impression 기록, 클릭 시 click 기록(둘 다 ad_stats → 트리거로 자동 과금 연결).
 * 인앱 캠페인(ad)이 없으면 AdSense로 폴백 — "인앱 우선, 빈 자리만 AdSense로 채우는" 워터폴.
 * AdSense 클라이언트 ID가 아직 설정 안 돼있으면 GoogleAdSense가 알아서 아무것도 안 그림.
 * @param {{ ad: object|null, variant?: 'feed'|'mini'|'banner', slotKey?: string, seed?: number }} props
 */
function PlacementAdCard({ ad, variant = 'feed', slotKey, seed = 0 }) {
  const { t, language, selectedRegions } = useMingo();
  const adStatsCtx = useMemo(
    () => ({ language, regions: selectedRegions, type: 'feed', category: slotKey || 'placement_ad' }),
    [language, selectedRegions, slotKey],
  );

  useEffect(() => {
    if (!ad) return;
    recordAdImpression({ id: ad.id, business_id: ad.businessId, title: ad.title, image: ad.image }, adStatsCtx);
  }, [ad, adStatsCtx]);

  if (!ad) {
    return (
      <GoogleAdSense
        variant={ADSENSE_VARIANT[variant] || 'card'}
        seed={seed}
        category={slotKey || 'placement_ad'}
      />
    );
  }

  const paidNotice = t('ads.paidNotice');

  // 기존 AdCard.js와 동일한 클래스명 재사용 — 채팅/커뮤니티/프로필 화면엔 이미 이 클래스로
  // 스타일이 잡혀있어서(mingo-chat-list__ad-item .mingo-ad-card 등) 새 CSS 없이도 바로 어울림.
  // 단, AdCard.js는 외부링크(target=_blank)라 내부 라우트(/business/:id)엔 안 맞아서 Link로 교체.
  return (
    <Link
      to={ad.link}
      className={`mingo-ad-card-link mingo-placement-ad--${variant}`}
      onClick={() => recordAdClick({ id: ad.id, business_id: ad.businessId, title: ad.title, image: ad.image }, adStatsCtx)}
      aria-label={ad.title || t('ads.badge')}
    >
      <article className="mingo-ad-card">
        <div className="mingo-ad-card__thumb">
          <img src={ad.image} alt={ad.title || ''} loading="lazy" />
          <span className="mingo-ad-card__badge">
            {t('ads.badge')}
            <button
              type="button"
              className="mingo-ad-card__badge-info"
              aria-label={paidNotice}
              title={paidNotice}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.alert(paidNotice);
              }}
            >
              <IconInfoCircle size={12} stroke={2} aria-hidden />
            </button>
          </span>
        </div>
        <div className="mingo-ad-card__body">
          <p className="mingo-ad-card__title">{ad.title}</p>
          {variant !== 'mini' && ad.description ? <p className="mingo-ad-card__desc">{ad.description}</p> : null}
        </div>
      </article>
    </Link>
  );
}

export default React.memo(PlacementAdCard);
